import { Injectable } from '@angular/core';

/**
 * Represents an object to be stored in local storage with additional metadata.
 *
 * This interface is useful for handling structured data in local storage. Each object
 * contains a key for easy retrieval, a value to store any type of data, a timeout
 * duration to define expiration, and a type to specify its data category.
 *
 * @interface LocalStorageObject
 * @property {string} key - A unique identifier used to retrieve the stored object from local storage.
 * @property {unknown} value - The data to be stored, which can be of any type.
 * @property {number} timeOut - The duration in milliseconds after which the stored data is considered expired.
 * @property {string} type - A descriptor to denote the type or category of the stored data.
 */
interface LocalStorageObject {
  key: string;
  value: unknown;
  timeOut: number;
  type: string;
}

/**
 * Service to interact with the browser's local storage. Provides methods to store and retrieve data with optional
 * expiration functionality.
 */
@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  constructor() {}

  /**
   * Stores a value in the browser's local storage with a specified timeout.
   *
   * @param {string} key - The key under which the value will be stored.
   * @param {unknown} value - The value to be stored. Can be a string, number, boolean, or object.
   * @param {number} [timeOut=86400] - The timeout in milliseconds after which the item is considered expired. Defaults to 86,400 s if not provided or invalid.
   * @return {void} Does not return any value.
   */
  public set(key: string, value: unknown, timeOut: number = 86400): void {
    let storageValue;
    if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
      storageValue = value.toString();
    }
    else {
      storageValue = value;
    }

    if (timeOut < 1) {
      timeOut = 86400;
    }

    const storageObject: LocalStorageObject = {
      key: key,
      value: storageValue,
      timeOut: new Date().getTime() + timeOut * 1000,
      type: typeof value
    };

    localStorage.setItem(key, JSON.stringify(storageObject));
  }

  /**
   * Retrieves a value by its key from the local storage if it has not expired.
   *
   * @param {string} key - The key associated with the value in the local storage.
   * @return {T | undefined} The value retrieved from the local storage if it exists and has not expired; otherwise, undefined.
   */
  public get<T>(key: string): T | undefined {
    const storageValue = localStorage.getItem(key);
    if (storageValue) {
      const storageObject: LocalStorageObject = JSON.parse(storageValue);
      if (new Date().getTime() > storageObject.timeOut) {
        localStorage.removeItem(key);
        return undefined;
      }

      return storageObject.value as T;
    }

    return undefined;
  }
}
